import { useEffect, useState } from 'react'
import { useTasks } from '../context/TaskContext'
import StatsCard from '../components/StatsCard'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { taskApi } from '../services/api'

function Stats() {
  const { stats } = useTasks()
  const [productivityData, setProductivityData] = useState(null)
  const [timeRange, setTimeRange] = useState(7)

  useEffect(() => {
    const loadProductivity = async () => {
      try {
        const data = await taskApi.getProductivity(timeRange)
        setProductivityData(data)
      } catch (error) {
        console.error('Failed to load productivity data:', error)
      }
    }
    loadProductivity()
  }, [timeRange])

  // Prepare chart data
  const weeklyData = stats?.weeklyData?.map(d => ({
    date: new Date(d.date).toLocaleDateString('en-US', { weekday: 'short' }),
    completed: d.completed,
  })) || []

  const priorityData = stats?.priorityDistribution?.map(p => ({
    name: p.priority,
    value: p.count,
  })) || []

  const categoryData = stats?.categoryDistribution?.map(c => ({
    name: c.category,
    value: c.count,
  })) || []

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Statistics
        </h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(Number(e.target.value))}
          className="form-select w-full sm:w-40"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Tasks"
          value={stats?.total || 0}
          subtitle="All time"
          icon="📊"
          color="blue"
        />
        <StatsCard
          title="Completion Rate"
          value={`${stats?.completionRate || 0}%`}
          subtitle={`${stats?.completed || 0} completed`}
          icon="✅"
          color="green"
        />
        <StatsCard
          title="Current Streak"
          value={stats?.streak?.current || 0}
          subtitle="days in a row"
          icon="🔥"
          color="purple"
        />
        <StatsCard
          title="Overdue"
          value={productivityData?.overdueCount || 0}
          subtitle="tasks pending"
          icon="⚠️"
          color="red"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Completion Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Tasks Completed
          </h3>
          {weeklyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="completed" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              No data available
            </div>
          )}
        </div>

        {/* Priority Distribution */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Priority Distribution
          </h3>
          {priorityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={priorityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {priorityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              No data available
            </div>
          )}
          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            {priorityData.map((entry, index) => (
              <div key={entry.name} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                  {entry.name} ({entry.value})
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Tasks by Category
        </h3>
        {categoryData.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {categoryData.map((cat, index) => (
              <div 
                key={cat.name}
                className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <div 
                  className="w-12 h-12 mx-auto rounded-full flex items-center justify-center text-2xl mb-2"
                  style={{ backgroundColor: `${COLORS[index % COLORS.length]}20` }}
                >
                  {cat.name === 'work' && '💼'}
                  {cat.name === 'personal' && '🏠'}
                  {cat.name === 'health' && '💪'}
                  {cat.name === 'shopping' && '🛒'}
                  {cat.name === 'general' && '📝'}
                  {!['work', 'personal', 'health', 'shopping', 'general'].includes(cat.name) && '📌'}
                </div>
                <p className="font-medium text-gray-900 dark:text-white capitalize">{cat.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{cat.value} tasks</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No category data available
          </div>
        )}
      </div>

      {/* Productivity Insights */}
      {productivityData && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Productivity Insights
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-600 dark:text-blue-400">Average Completion Time</p>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">
                {productivityData.avgCompletionTime 
                  ? `${Math.round(productivityData.avgCompletionTime)}h` 
                  : 'N/A'}
              </p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-sm text-green-600 dark:text-green-400">Tasks Completed</p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-300">
                {productivityData.dailyCompletion?.reduce((sum, d) => sum + d.completed, 0) || 0}
              </p>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <p className="text-sm text-purple-600 dark:text-purple-400">Total Time Logged</p>
              <p className="text-2xl font-bold text-purple-900 dark:text-purple-300">
                {Math.round((productivityData.dailyCompletion?.reduce((sum, d) => sum + (d.total_minutes || 0), 0) || 0) / 60)}h
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Stats